// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react'
import { navigate } from 'gatsby'
import { useLocation } from '@reach/router'
import { LoginForm, TwoFactorForm } from 'components/LoginSignUpForm'
import { FormData, SessionDuration } from 'components/LoginSignUpForm/LoginSignUpForm.interface'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { setSessionTtl, setWasRememberMeSelected } from 'store/session/session'
import { getDocumentHead } from 'components/DocumentHead'
import { useGetDevicesQuery } from 'store/api/devices'
import useGetUserState from 'store/api/user/useGetUserState'
import { clearErrors } from 'store/errors'

export const twoFAErrorMessage = '2FA is enabled. Please enter a valid 2FA code.'

export default function LoginPage(): React.ReactElement {
  const dispatch = useAppDispatch()
  const sessionToken = useAppSelector(s => s.session.sessionToken)
  const sessionTtl = useAppSelector(s => s.session.sessionTtl)
  const errors = useAppSelector(s => s.errors.errors)
  const location = useLocation()
  const [form, setForm] = useState<FormData>({
    email: '',
    password: '',
    code: '',
  })
  const [shouldShowTwoFaForm, setShouldShowTwoFaForm] = useState(false)
  // fetch all devices so that we can check if any of them is configured
  const { data: devicesData } = useGetDevicesQuery('', { skip: !sessionToken })
  const resolverConfirmed = devicesData?.devices?.some(d => d.status)
  const { sessionLogin, isLoginLoading, isSsoLoginLoading } = useGetUserState()
  const [lastGlobalError] = errors
  /* this will be set to true if we are coming from okta callback page and the login failed */
  const { doNotClearErrors } = (location?.state as { doNotClearErrors?: boolean }) ?? {}

  useEffect(() => {
    /**
     * Avoid showing "Email already exists, please login" or any other residual
     * errors when returning to login page.     *
     */
    if (!doNotClearErrors) {
      dispatch(clearErrors())
    }
    // clear the error if navigating between pages
    // https://gitlab.int.windscribe.com/controld/frontend/-/issues/320
    return () => {
      dispatch(clearErrors())
    }
  }, [dispatch, doNotClearErrors, shouldShowTwoFaForm])

  // load ttl value from store
  useEffect(() => {
    if (sessionTtl) {
      setForm(prevForm => ({ ...prevForm, ttl: sessionTtl as SessionDuration }))
    } else {
      setForm(prevForm => ({ ...prevForm, ttl: SessionDuration.MONTH }))
    }
  }, [sessionTtl])

  useEffect(() => {
    if (sessionToken) {
      if (location.state?.pathname) {
        navigate(
          `${location.state.pathname}${
            resolverConfirmed
              ? location.state.search
              : location.state.search.includes('token')
              ? location.state.search
              : ``
          }`,
          { state: { login: true } },
        )
      } else {
        navigate('/dashboard/profiles', {
          state: { login: true },
        })
      }
    } else if (lastGlobalError?.message === twoFAErrorMessage) {
      if (!form.code) {
        dispatch(clearErrors())
      }
      setShouldShowTwoFaForm(true)
    }
  }, [dispatch, form.code, lastGlobalError?.message, location, resolverConfirmed, sessionToken])

  const setFormValue = useCallback((target): void => {
    setForm(prevForm => {
      return {
        ...prevForm,
        [target.name]: target.value,
      }
    })
  }, [])

  const handleSubmit = (event: React.FormEvent): void => {
    dispatch(clearErrors())
    const { email, password, ttl, code } = form
    event.preventDefault()
    dispatch(setWasRememberMeSelected(ttl !== SessionDuration.NEVER))
    if (!isLoginLoading) {
      sessionLogin(
        code ? form : { email, password, ttl: ttl === SessionDuration.NEVER ? null : ttl },
      )
      if (!code) {
        // save session ttl
        dispatch(setSessionTtl(ttl))
      }
    }
  }

  const isTwoFAError = lastGlobalError?.message?.includes(twoFAErrorMessage)

  if (sessionToken) {
    return null
  }

  return shouldShowTwoFaForm ? (
    <TwoFactorForm
      handleSubmit={handleSubmit}
      handleUpdate={setFormValue}
      error={lastGlobalError?.message}
      form={form}
      isLoading={isLoginLoading}
    />
  ) : (
    <LoginForm
      handleSubmit={handleSubmit}
      error={
        !isTwoFAError ? (lastGlobalError ? lastGlobalError.message : lastGlobalError?.message) : ''
      }
      handleUpdate={setFormValue}
      form={form}
      isLoading={isLoginLoading}
      isSsoLoginLoading={isSsoLoginLoading}
    />
  )
}

export const Head = getDocumentHead({ title: 'Log in' })
